import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useDispatch, useSelector } from "react-redux";
import { getDocuments } from "../../../../../store/document/useCases/getDocuments/action";
import { selectDocument } from "../../../../../store/document/repository/selector";
import { getCategoriesWithoutTree } from "../../../../../store/category/useCases/getCategoriesWithoutTree/action";
import { selectCategory } from "../../../../../store/category/repository/selector";
import { DocumentFilters } from "../../../../../types/documentList";
import { deleteDocument } from "../../../../../store/document/useCases/deleteDocument/action";
import { deleteDocumentHard } from "../../../../../store/document/useCases/deleteDocumentHard/action";
import { updateDocument } from "../../../../../store/document/useCases/updateDocument/action";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../../locales/tokens";
import { TIMESTAMP_DAY } from "../../../../../config";
import { uploadReport } from "../../../../../store/document/useCases/uploadReport/action";
import { selectOrgStructure } from "../../../../../store/orgStructure/repository/selector";
import { updateOrgStructure } from "../../../../../store/orgStructure/useCases/updateOrgStructure/action";
import { setDocuments } from "../../../../../store/document/repository/slice";
import { useSearchParams } from "react-router-dom";

export const useDocumentsList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchText, setSearchText] = useState("");

  // const getInitialFilters = (): DocumentFilters => {
  //   return {
  //     page: Number(searchParams.get("page")) || 1,
  //     rowsPerPage: Number(searchParams.get("rowsPerPage")) || 30,
  //     query: searchParams.get("query") || "",
  //     tab: (searchParams.get("tab") as "Active" | "Deleted") || "Active",
  //     category: searchParams.get("category")
  //       ? JSON.parse(searchParams.get("category")!)
  //       : null,
  //     type: searchParams.get("type")
  //       ? JSON.parse(searchParams.get("type")!)
  //       : null,
  //     status: searchParams.get("status")
  //       ? JSON.parse(searchParams.get("status")!)
  //       : null,
  //     initiator: searchParams.get("initiator")
  //       ? JSON.parse(searchParams.get("initiator")!)
  //       : null,
  //     date: searchParams.get("date") ? Number(searchParams.get("date")) : null,
  //   };
  // };

  const [filters, updateFilters] = useState<DocumentFilters>({
    page: Number(searchParams.get("page")) || 1,
    rowsPerPage: Number(searchParams.get("rowsPerPage")) || 30,
    query: searchParams.get("query") || "",
    tab: (searchParams.get("tab") as "Active" | "Deleted") || "Active",
    category: searchParams.get("category")
      ? JSON.parse(searchParams.get("category")!)
      : null,
    type: searchParams.get("type")
      ? JSON.parse(searchParams.get("type")!)
      : null,
    status: searchParams.get("status")
      ? JSON.parse(searchParams.get("status")!)
      : null,
    initiator: searchParams.get("initiator")
      ? JSON.parse(searchParams.get("initiator")!)
      : null,
    date: searchParams.get("date") ? Number(searchParams.get("date")) : null,
  });

  useEffect(() => {
    setSearchText(filters.query);
  }, [filters.query]);

  const {
    documentsList,
    documentsCount,
    isLoading,
    deleteDocumentError,
    updateDocumentError,
    getDocumentError,
  } = useSelector(selectDocument);
  const { categoryList } = useSelector(selectCategory);
  const { orgStructure } = useSelector(selectOrgStructure);
  const [pageAmount, setPageAmount] = useState(0);
  const [updateClick, setUpdateClick] = useState(false);
  const [deleteClick, setDeleteClick] = useState(false);
  const [documentId, setDocumentId] = useState(0);
  const [isProccessDocument, setProcessDocuments] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const params: Record<string, string> = {};

    if (filters.page) params.page = String(filters.page);
    if (filters.rowsPerPage) params.rowsPerPage = String(filters.rowsPerPage);
    if (filters.query) params.query = filters.query;
    if (filters.tab) params.tab = filters.tab;
    if (filters.category) params.category = JSON.stringify(filters.category);
    if (filters.type) params.type = JSON.stringify(filters.type);
    if (filters.status) params.status = JSON.stringify(filters.status);
    if (filters.initiator) params.initiator = JSON.stringify(filters.initiator);
    if (filters.date) params.date = String(filters.date);

    setSearchParams(params);
  }, [filters, setSearchParams]);

  useEffect(() => {
    dispatch(getDocuments(combineRequestBody()));
    setProcessDocuments(true);
  }, [filters]);

  const combineRequestBody = () => {
    const body: any = {
      page: filters.page,
      perPage: filters.rowsPerPage,
      name: filters.query,
    };
    if (filters.category) body.categoryId = filters.category.id;
    if (filters.initiator) body.initiatorId = filters.initiator.value;
    if (filters.type) body.type = filters.type.value;
    if (filters.status) body.status = filters.status.value;
    if (filters.date) {
      const startDateString = new Date(filters.date).toISOString();
      body.updatedAtStart = startDateString;
      const endDateString = new Date(
        filters.date + TIMESTAMP_DAY,
      ).toISOString();
      body.updatedAtEnd = endDateString;
    }
    body.isDeleted = filters.tab === "Active" ? false : true;
    return body;
  };

  useEffect(() => {
    dispatch(getCategoriesWithoutTree());
    dispatch(updateOrgStructure());
  }, []);

  useEffect(() => {
    if (documentsCount) {
      setPageAmount(Math.ceil(documentsCount / filters.rowsPerPage));
    }
  }, [documentsCount]);

  useEffect(() => {
    debouncedHandleDocumentSeacrh(searchText);
  }, [searchText]);

  useEffect(() => {
    if (!isLoading && isProccessDocument) {
      if (getDocumentError) toast.error(getDocumentError);
      setTimeout(() => setProcessDocuments(false), 400);
    }
  }, [getDocumentError, isLoading]);

  useEffect(() => {
    if (!isLoading && updateClick) {
      if (updateDocumentError) {
        toast.error(updateDocumentError);
      } else {
        toast.success(t(tokens.documents.messages.documentUpdated));
        dispatch(getDocuments(combineRequestBody()));
      }
      setUpdateClick(false);
    }
  }, [updateDocumentError, isLoading]);

  useEffect(() => {
    if (!isLoading && deleteClick) {
      if (deleteDocumentError) {
        toast.error(deleteDocumentError);
      } else {
        filters.tab === "Active"
          ? toast.success(t(tokens.documents.messages.documentRemoved))
          : toast.success(t(tokens.documents.messages.documentRemovedHard));
        dispatch(getDocuments(combineRequestBody()));
      }
      setDeleteClick(false);
      setDocumentId(0);
    }
  }, [deleteDocumentError, isLoading]);

  const handleSearchDocumentText = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const onSearchDocument = (value: string) => {
    const query = value || "";
    if (query.length !== 1) {
      updateFilters((prev) => ({
        ...prev,
        query,
        // page: 1
      }));
      scrollTopList();
    }
  };

  const { debouncedWrapper: debouncedHandleDocumentSeacrh } = useDebounce(
    onSearchDocument,
    500,
  );

  const handleTabsChange = (event: ChangeEvent<{}>, tab: string): void => {
    updateFilters((prev) => ({
      ...prev,
      tab: tab as "Active" | "Deleted",
      // page: 1,
    }));
    scrollTopList();
  };

  const changeType = useCallback(
    (type: any) => {
      updateFilters((prev) => ({ ...prev, page: 1, type }));
      scrollTopList();
    },
    [updateFilters],
  );

  const changeStatus = useCallback(
    (status: any) => {
      updateFilters((prev) => ({
        ...prev,
        // page: 1,
        status,
      }));
      scrollTopList();
    },
    [updateFilters],
  );

  const handlePageChange = useCallback(
    (event: ChangeEvent<unknown>, page: number): void => {
      updateFilters((prev) => ({ ...prev, page }));
      scrollTopList();
    },
    [updateFilters],
  );

  const handleChangeCategory = useCallback(
    (category: any) => {
      updateFilters((prev) => ({
        ...prev,
        // page: 1,
        category,
      }));
      category === null && dispatch(getCategoriesWithoutTree());
      scrollTopList();
    },
    [updateFilters],
  );

  const changeCategoryText = (event: ChangeEvent<HTMLInputElement>) => {
    debouncedHandleCategorySeacrh(event.target.value);
  };

  const onSearchCategory = (value: string) => {
    if (value.length) {
      dispatch(getCategoriesWithoutTree({ name: value }));
    } else {
      dispatch(getCategoriesWithoutTree());
    }
  };

  const { debouncedWrapper: debouncedHandleCategorySeacrh } = useDebounce(
    onSearchCategory,
    700,
  );

  const handleDateChange = useCallback(
    (date: Date | null): void => {
      const currentDate = date?.valueOf() || null;
      updateFilters((prev) => ({
        ...prev,
        // page: 1,
        date: currentDate,
      }));
      scrollTopList();
    },
    [updateFilters],
  );

  const resetFilters = useCallback((): void => {
    updateFilters({
      page: filters.page,
      rowsPerPage: 30,
      query: "",
      tab: "Active",
      category: null,
      type: null,
      status: null,
      initiator: null,
      date: null,
    });
    setSearchParams({});
    scrollTopList();
  }, [setSearchParams]);

  const handleStatus = (id: number, status: string) => {
    const documentStatus = status === "Hidden" ? "Available" : "Hidden";
    dispatch(updateDocument({ body: { id, status: documentStatus } }));
    setUpdateClick(true);
  };

  const handleDelete = (id: number, mode?: string) => {
    if (!mode) {
      dispatch(deleteDocument({ id }));
    } else {
      dispatch(deleteDocumentHard({ id }));
    }
    setDocumentId(id);
    setDeleteClick(true);
  };

  const handleUploadReport = (id: number, name: string) => {
    dispatch(uploadReport({ id, name }));
  };

  const onSelectInitiator = (selectedKeysValue: string, node: any) => {
    if ("children" in node) return;
    updateFilters((prev) => ({
      ...prev,
      // page: 1,
      initiator: node,
    }));
    scrollTopList();
  };

  const onChangeInitiator = () => {
    if (filters.initiator) {
      updateFilters((prev) => ({ ...prev, page: 1, initiator: null }));
    }
  };

  const scrollTopList = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };

  return {
    filters,
    searchText,
    handleTabsChange,
    handleSearchDocumentText,
    handleChangeCategory,
    changeType,
    changeStatus,
    handlePageChange,
    documentsList,
    categoryList,
    orgStructure,
    changeCategoryText,
    handleDateChange,
    pageAmount,
    onChangeInitiator,
    handleUploadReport,
    handleStatus,
    resetFilters,
    handleDelete,
    onSelectInitiator,
    deleteClick,
    documentId,
    isProccessDocument,
    scrollRef,
  };
};
